import { Controller } from "@hotwired/stimulus";
import { getJSON, listResults, sendJSON, errorDetail } from "../api.js";

// Panel „Klienci" (staff): drzewko organizacja → użytkownicy z /api/staff/clients/.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "modal",
    "timeline",
    "timelineStatus",
    // edycja (F1b)
    "editModal",
    "editTitle",
    "editError",
    "orgFields",
    "userFields",
    "orgName",
    "orgNip",
    "orgStatus",
    "orgInvoice",
    "userName",
    "userEmail",
    "userPhone",
    "userActive",
    "userRole",
  ];
  static values = { url: String };

  connect() {
    if (this.urlValue) this.load();
  }

  async load() {
    try {
      this.#render(listResults(await getJSON(this.urlValue)));
    } catch {
      this.statusTarget.textContent = "Nie udało się załadować klientów. Odśwież stronę.";
    }
  }

  #render(orgs) {
    // Zapamiętaj drzewko, by modal edycji mógł wypełnić formularz bez ponownego GET-a.
    this._orgs = orgs;
    this.rowsTarget.textContent = "";
    if (!orgs.length) {
      this.statusTarget.textContent = "Brak klientów.";
      return;
    }
    this.statusTarget.hidden = true;
    const orgTpl = document.getElementById("staff-client-org-template");
    const userTpl = document.getElementById("staff-client-user-template");
    for (const org of orgs) {
      const node = orgTpl.content.firstElementChild.cloneNode(true);
      node.querySelector('[data-field="name"]').textContent = org.name;
      node.querySelector('[data-field="nip"]').textContent = org.nip || "—";
      node.querySelector('[data-field="count"]').textContent =
        `${org.users.length} użytk.`;
      node
        .querySelector('[data-field="activityBtn"]')
        .setAttribute("data-staff-clients-org-param", org.id);
      node
        .querySelector('[data-field="editOrgBtn"]')
        .setAttribute("data-staff-clients-org-param", org.id);
      const usersBox = node.querySelector('[data-field="users"]');
      for (const user of org.users) {
        const row = userTpl.content.firstElementChild.cloneNode(true);
        row.querySelector('[data-field="name"]').textContent = user.name || "—";
        row.querySelector('[data-field="email"]').textContent = user.email;
        row.querySelector('[data-field="phone"]').textContent = user.phone || "—";
        row.querySelector('[data-field="role"]').textContent = user.role;
        row.querySelector('[data-field="active"]').textContent = user.is_active
          ? "aktywny"
          : "nieaktywny";
        row
          .querySelector('[data-field="editUserBtn"]')
          .setAttribute("data-staff-clients-member-param", user.id);
        usersBox.appendChild(row);
      }
      this.rowsTarget.appendChild(node);
    }
  }

  // --- Edycja organizacji / członka (F1b) ---

  editOrg(event) {
    const org = (this._orgs || []).find((o) => o.id === event.params.org);
    if (!org) return;
    this._editMode = "org";
    this._editId = org.id;
    this.editTitleTarget.textContent = "Edycja organizacji";
    this.orgNameTarget.value = org.name || "";
    this.orgNipTarget.value = org.nip || "";
    this.orgStatusTarget.value = org.status || "pending";
    this.orgInvoiceTarget.value = org.invoice_address || "";
    this.orgFieldsTarget.hidden = false;
    this.userFieldsTarget.hidden = true;
    this.#resetEditError();
    this.#openModalEl(this.editModalTarget);
  }

  editUser(event) {
    const member = this.#findMember(event.params.member);
    if (!member) return;
    this._editMode = "user";
    this._editId = member.id;
    this.editTitleTarget.textContent = "Edycja użytkownika";
    this.userNameTarget.value = member.name || "";
    this.userEmailTarget.value = member.email || "";
    this.userPhoneTarget.value = member.phone || "";
    this.userActiveTarget.checked = Boolean(member.is_active);
    this.userRoleTarget.value = member.role || "member";
    this.orgFieldsTarget.hidden = true;
    this.userFieldsTarget.hidden = false;
    this.#resetEditError();
    this.#openModalEl(this.editModalTarget);
  }

  #findMember(id) {
    for (const org of this._orgs || []) {
      const found = (org.users || []).find((u) => u.id === id);
      if (found) return found;
    }
    return null;
  }

  async save() {
    let url;
    let body;
    if (this._editMode === "org") {
      url = `${this.urlValue}${this._editId}/`;
      body = {
        name: this.orgNameTarget.value,
        nip: this.orgNipTarget.value,
        status: this.orgStatusTarget.value,
        invoice_address: this.orgInvoiceTarget.value,
      };
    } else {
      url = `/api/staff/client-members/${this._editId}/`;
      body = {
        name: this.userNameTarget.value,
        email: this.userEmailTarget.value,
        phone: this.userPhoneTarget.value,
        is_active: this.userActiveTarget.checked,
        role: this.userRoleTarget.value,
      };
    }
    const { ok, data } = await sendJSON("PATCH", url, body);
    if (!ok) {
      this.editErrorTarget.hidden = false;
      this.editErrorTarget.textContent = errorDetail(
        data,
        "Nie udało się zapisać zmian. Sprawdź dane i spróbuj ponownie.",
      );
      return;
    }
    await this.load();
    this.closeEdit();
  }

  #resetEditError() {
    this.editErrorTarget.hidden = true;
    this.editErrorTarget.textContent = "";
  }

  closeEdit() {
    if (this.hasEditModalTarget) this.#closeModalEl(this.editModalTarget);
  }

  backdropCloseEdit(event) {
    if (event.target === this.editModalTarget) this.closeEdit();
  }

  toggle(event) {
    const head = event.currentTarget;
    const org = head.closest(".ls-client-org");
    const users = org.querySelector('[data-field="users"]');
    users.hidden = !users.hidden;
    head.querySelector('[data-field="caret"]').textContent = users.hidden
      ? "▸"
      : "▾";
  }

  // --- Aktywność / historia (F1c + F1d) ---

  async activity(event) {
    const orgId = event.params.org;
    this.timelineTarget.textContent = "";
    this.timelineStatusTarget.hidden = false;
    this.timelineStatusTarget.textContent = "Ładowanie…";
    this.#openModal();
    try {
      this.#renderTimeline(await getJSON(`${this.urlValue}${orgId}/activity/`));
    } catch {
      this.timelineStatusTarget.textContent = "Nie udało się załadować aktywności.";
    }
  }

  #renderTimeline(events) {
    if (!events.length) {
      this.timelineStatusTarget.textContent = "Brak aktywności.";
      return;
    }
    this.timelineStatusTarget.hidden = true;
    const tpl = document.getElementById("staff-client-event-template");
    for (const event of events) {
      const row = tpl.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="at"]').textContent = this.#formatDate(event.at);
      row.querySelector('[data-field="kind"]').textContent =
        event.kind === "auth" ? "logowanie" : "zmiana";
      row.querySelector('[data-field="desc"]').textContent = this.#describe(event);
      this.timelineTarget.appendChild(row);
    }
  }

  #describe(event) {
    const who = event.actor || "—";
    if (event.kind === "auth") {
      const map = { login: "zalogował się", logout: "wylogował się", login_failed: "nieudane logowanie" };
      return `${who} — ${map[event.action] || event.action}`;
    }
    const changes = event.metadata && event.metadata.changes;
    if (event.action === "client.updated" && changes) {
      const parts = Object.entries(changes).map(
        ([field, delta]) => `${field}: „${delta.from}" → „${delta.to}"`,
      );
      return `${who} — edycja (${parts.join(", ")})`;
    }
    return `${who} — ${event.action}`;
  }

  #formatDate(iso) {
    return new Date(iso).toLocaleString("pl-PL", {
      dateStyle: "short",
      timeStyle: "short",
    });
  }

  #openModal() {
    this.#openModalEl(this.modalTarget);
  }

  // Wspólne otwieranie/zamykanie overlayu (animacja is-open) — dla modala aktywności i edycji.
  #openModalEl(el) {
    el.hidden = false;
    this._raf = requestAnimationFrame(() => el.classList.add("is-open"));
  }

  #closeModalEl(el) {
    if (this._raf) cancelAnimationFrame(this._raf);
    el.classList.remove("is-open");
    setTimeout(() => {
      if (!el.classList.contains("is-open")) el.hidden = true;
    }, 180);
  }

  closeActivity() {
    if (this.hasModalTarget) this.#closeModalEl(this.modalTarget);
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) this.closeActivity();
  }

  stop(event) {
    event.stopPropagation();
  }
}
